import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import { useCreateImportantDateMutation } from '@/app/(main)/client-detail/components/tabs/hooks/useCreateImportantDateMutation';
import { useDeleteImportantDateMutation } from '@/app/(main)/client-detail/components/tabs/hooks/useDeleteImportantDateMutation';
import { IMPORTANT_DATES_BY_CLIENT_QUERY_KEY } from '@/app/(main)/client-detail/components/tabs/hooks/useGetImportantDatesByClientQuery';
import { useUpdateImportantDateMutation } from '@/app/(main)/client-detail/components/tabs/hooks/useUpdateImportantDateMutation';
import { CLIENT_GROUPED_QUERY_KEY } from '@/app/(main)/client-detail/hooks/useGetClientGroupedQuery';
import type { ClientDataWithAdditionalData, GetClientDataWithAdditionalData } from '../../../context/types';
import { IMPORTANT_DATES_QUERY_KEY } from '../../../hooks/useGetImportantDates';
import { EditImportantDatesForm } from '../../EditSectionModals/components/EditImportantDatesForm';
import { ModalKeys } from '../../EditSectionModals/types';
import { defineSection } from '../../EditSectionModals/utils/defineSection';
import { getImportantDatesPayload } from '../../EditSectionModals/utils/getImportantDatesPayload';
import { CLIENT_DETAILS_QUERY_KEY } from '@/app/(main)/client-detail/consts/client-detail';
import type { ImportantDatesFormValues, ImportantDatesPayload } from './types';

const schema = yup.object({
  clientId: yup.number().required(),
  important_dates: yup.array().of(
    yup.object({
      id: yup.number().nullable().defined(),
      name: yup.string().required('Name is required').trim(),
      event_date: yup.string().required('Date is required'),
      event_frequency_id: yup
        .number()
        .nullable()
        .test('required', 'Frequency is required', (val) => val != null),
      notes: yup.string().nullable().optional(),
    })
  ),
});

export type ImportantDatesSectionFormValues = yup.InferType<typeof schema>;

async function invalidateImportantDatesCaches(queryClient: QueryClient) {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: [IMPORTANT_DATES_BY_CLIENT_QUERY_KEY],
      refetchType: 'all',
    }),
    queryClient.invalidateQueries({
      queryKey: [CLIENT_DETAILS_QUERY_KEY],
      refetchType: 'all',
    }),
    queryClient.invalidateQueries({
      queryKey: [IMPORTANT_DATES_QUERY_KEY],
      refetchType: 'all',
    }),
  ]);
}

function useImportantDatesOnSave() {
  const createMutation = useCreateImportantDateMutation();
  const updateMutation = useUpdateImportantDateMutation();
  const deleteMutation = useDeleteImportantDateMutation();
  const queryClient = useQueryClient();

  return async (payload: ImportantDatesPayload) => {
    const { clientId, items } = payload;

    const deletes = items.filter((i) => i.action === 'delete');
    const creates = items.filter((i) => i.action === 'create');
    const updates = items.filter((i) => i.action === 'update');

    for (const item of deletes) {
      if (item.id != null) {
        await deleteMutation.mutateAsync({ id: item.id, clientId });
      }
    }
    for (const item of creates) {
      await createMutation.mutateAsync({
        name: item.name,
        event_date: item.event_date,
        event_frequency_id: item.event_frequency_id,
        client_id: clientId,
        notes: item.notes ?? undefined,
      });
    }
    for (const item of updates) {
      if (item.id == null) continue;
      await updateMutation.mutateAsync({
        id: item.id,
        clientId,
        name: item.name,
        event_date: item.event_date,
        event_frequency_id: item.event_frequency_id,
        notes: item.notes ?? undefined,
      });
    }

    await invalidateImportantDatesCaches(queryClient);

    toast.success('Important dates updated successfully');
    queryClient.invalidateQueries({ queryKey: [CLIENT_GROUPED_QUERY_KEY] });
  };
}

function mapValuesToPayload(
  values: ImportantDatesSectionFormValues,
  client?: ClientDataWithAdditionalData | null
): ImportantDatesPayload {
  const clientId = values.clientId;
  const normalized: ImportantDatesFormValues = {
    clientId,
    important_dates: (values.important_dates ?? []) as ImportantDatesFormValues['important_dates'],
  };
  const initialImportantDates = client?.important_dates ?? [];
  const items = getImportantDatesPayload(normalized, {
    initialImportantDates: initialImportantDates.filter((d) => d.id).map((d) => ({ id: d.id })),
  });
  return { clientId, items };
}

export const importantDatesSection = defineSection<
  (typeof ModalKeys)['important_dates'],
  ClientDataWithAdditionalData,
  typeof schema,
  ImportantDatesPayload
>({
  key: ModalKeys.important_dates,
  title: 'Important dates',
  subtitle: 'Manage important client travel dates and recurring events.',
  schema,
  mapClientToValues: (client) => ({
    clientId:
      client?.primary_passenger?.client?.client_id ?? (client as unknown as GetClientDataWithAdditionalData)?.id ?? 0,
    important_dates: client?.important_dates ?? [],
  }),
  mapValuesToPayload,
  useOnSave: useImportantDatesOnSave,
  Fields: EditImportantDatesForm,
});
